import { Injectable, inject, PLATFORM_ID, signal, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private keycloak: Keycloak | null = null;

  private readonly _authenticated = signal(false);
  private readonly _userProfile = signal<UserProfile | null>(null);
  private readonly _token = signal<string | null>(null);
  private readonly _initialized = signal(false);

  readonly authenticated = this._authenticated.asReadonly();
  readonly userProfile = this._userProfile.asReadonly();
  readonly token = this._token.asReadonly();
  readonly initialized = this._initialized.asReadonly();

  readonly isAdmin = computed(() => {
    const profile = this._userProfile();
    return profile?.roles.includes('admin') ?? false;
  });

  readonly isUser = computed(() => {
    const profile = this._userProfile();
    return profile?.roles.includes('user') ?? false;
  });

  /**
   * Keycloak initialisieren. Wird via APP_INITIALIZER aufgerufen.
   * check-sso: Prüft still ob eine Session existiert, erzwingt aber KEINEN Login.
   */
  async init(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      this._initialized.set(true);
      return true;
    }

    this.keycloak = new Keycloak({
      url: environment.keycloakUrl,
      realm: environment.keycloakRealm,
      clientId: environment.keycloakClientId,
    });

    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      });

      this._authenticated.set(authenticated);
      this._initialized.set(true);

      if (authenticated) {
        this.loadUserProfile();
        this._token.set(this.keycloak.token ?? null);
        this.setupTokenRefresh();
      }

      return true;
    } catch (error) {
      console.error('[Auth] Keycloak init fehlgeschlagen:', error);
      this._initialized.set(true);
      return false;
    }
  }

  login(): void {
    this.keycloak?.login({
      redirectUri: window.location.origin + '/dashboard',
    });
  }

  logout(): void {
    this.keycloak?.logout({
      redirectUri: window.location.origin + '/welcome',
    });
  }

  getToken(): string | undefined {
    return this.keycloak?.token;
  }

  async refreshToken(): Promise<string | undefined> {
    if (!this.keycloak) return undefined;
    try {
      await this.keycloak.updateToken(30);
      this._token.set(this.keycloak.token ?? null);
      return this.keycloak.token;
    } catch {
      console.error('[Auth] Token-Refresh fehlgeschlagen, Logout...');
      this.logout();
      return undefined;
    }
  }

  private loadUserProfile(): void {
    if (!this.keycloak?.authenticated) return;
    const tokenParsed = this.keycloak.tokenParsed;
    const realmRoles = tokenParsed?.realm_access?.roles ?? [];

    this._userProfile.set({
      id: tokenParsed?.sub ?? '',
      username: tokenParsed?.['preferred_username'] ?? '',
      email: tokenParsed?.['email'] ?? '',
      firstName: tokenParsed?.['given_name'] ?? '',
      lastName: tokenParsed?.['family_name'] ?? '',
      roles: realmRoles,
    });
  }

  private setupTokenRefresh(): void {
    setInterval(async () => {
      if (this.keycloak?.authenticated) {
        try {
          const refreshed = await this.keycloak.updateToken(70);
          if (refreshed) {
            this._token.set(this.keycloak.token ?? null);
          }
        } catch {
          console.error('[Auth] Token-Refresh fehlgeschlagen');
        }
      }
    }, 60_000);
  }
}
